import React, { useEffect } from 'react';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { NODE_KINDS } from 'features/evals3/editor/nodeKinds';
import { KindEditor } from 'features/evals3/editor/NodeConfigPanel';
import TagInput from 'features/evals3/editor/TagInput';
import { outcomeLabel } from 'shared/decision/routing';
import { Section, inlineRow as line, numInput, inputBox } from 'features/evals3/editor/formParts';
import {
  SIMPLE_KINDS, ANSWERS, FIXED_ANSWER, readSimple, startSimple, buildSimple, withAnswer, withDecisionOutput,
} from 'features/evals3/editor/simpleModel';

/**
 * A simple eval as a form: the check, its answer, which answers pass.
 *
 * `graph` is the eval's graph and `onChange` gets the next one — the same
 * graph the triage canvas would draw, so switching to Triage loses nothing.
 * The check's own editor is the step panel's (KindEditor): a judge's prompt
 * and model, Python with the trace fields, the semantic checks, a grader's
 * instructions. There is no Filter check: the eval's own filters already
 * decide which events are scored.
 */

const num = (raw, fallback) => { const n = Number(raw); return Number.isFinite(n) ? n : fallback; };

/**
 * The check to run: the five evaluator types in one segmented control, each
 * with its mark and accent from nodeKinds — the same mark the canvas and the
 * step palette draw, so a kind looks the same wherever it is picked.
 */
const KIND_OPTIONS = SIMPLE_KINDS.map((k) => {
  const m = NODE_KINDS[k];
  return { value: k, label: m.label, hint: m.blurb, icon: ({ size }) => <m.Icon size={size} style={{ color: m.accent }} aria-hidden /> };
});

function KindPicker({ value, onPick, readOnly }) {
  return (
    <SegmentedTabs ariaLabel="Eval type" value={value} options={KIND_OPTIONS} className="flex-wrap"
      onChange={(k) => { if (!readOnly) onPick(k); }} />
  );
}

/** The answer's shape: true/false, a score with its range, or the categories. */
function AnswerEditor({ form, onForm, readOnly }) {
  const fixed = FIXED_ANSWER[form.kind];
  const spec = form.spec;
  const setSpec = (next) => onForm(withAnswer(form, next));
  return (
    <>
      {!fixed && (
        <SegmentedTabs ariaLabel="Output" value={spec.type} onChange={(t) => {
          if (t === spec.type) return;
          setSpec(t === 'boolean' ? { type: 'boolean' } : t === 'number' ? { type: 'number', min: 0, max: 1 } : { type: 'enum', values: ['good', 'bad'] });
        }} options={ANSWERS} />
      )}
      {spec.type === 'number' && (
        <div className={`${line} ${fixed ? '' : 'mt-2'}`}>
          <span>A score from</span>
          <input type="number" step="any" value={spec.min} readOnly={readOnly} aria-label="Score from" className={numInput}
            onChange={(e) => setSpec({ ...spec, min: num(e.target.value, 0) })} />
          <span>to</span>
          <input type="number" step="any" value={spec.max} readOnly={readOnly} aria-label="Score to" className={numInput}
            onChange={(e) => setSpec({ ...spec, max: num(e.target.value, 1) })} />
        </div>
      )}
      {spec.type === 'enum' && !fixed && (
        <div className="mt-2">
          <TagInput value={spec.values || []} readOnly={readOnly} ariaLabel="Categories" placeholder="Type a category and press Enter"
            onChange={(values) => setSpec({ ...spec, values })} />
        </div>
      )}
      {spec.type === 'enum' && fixed && (
        <div className="text-[12px] text-[color:var(--text-secondary)]">One of: {(spec.values || []).join(', ') || '—'}</div>
      )}
      {spec.type === 'boolean' && fixed && <div className="text-[12px] text-[color:var(--text-secondary)]">True or false</div>}
    </>
  );
}

/** Which answers pass. */
function RuleEditor({ form, onForm, readOnly }) {
  const { spec, rule } = form;
  const setRule = (next) => onForm({ ...form, rule: next });
  if (spec.type === 'boolean') {
    return (
      <div className={line}>
        <span>Passes when the answer is</span>
        <SegmentedTabs ariaLabel="Passes when" value={rule.value === false ? 'false' : 'true'} onChange={(v) => { if (!readOnly) setRule({ type: 'boolean', value: v === 'true' }); }}
          options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]} />
      </div>
    );
  }
  if (spec.type === 'number') {
    return (
      <div className={line}>
        <span>Passes when the score is from</span>
        <input type="number" step="any" min={spec.min} max={spec.max} value={rule.min} readOnly={readOnly} aria-label="Passes from" className={numInput}
          onChange={(e) => setRule({ ...rule, min: num(e.target.value, spec.min) })} />
        <span>to</span>
        <input type="number" step="any" min={spec.min} max={spec.max} value={rule.max} readOnly={readOnly} aria-label="Passes to" className={numInput}
          onChange={(e) => setRule({ ...rule, max: num(e.target.value, spec.max) })} />
      </div>
    );
  }
  const values = spec.values || [];
  const chosen = new Set(rule.values || []);
  return (
    <div>
      <div className="mb-1.5 text-[12px] text-[color:var(--text-secondary)]">Passes when the answer is one of</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5" role="group" aria-label="Passing categories">
        {values.map((v) => (
          <label key={v} className="flex items-center gap-1.5 text-[12px] text-[color:var(--text-primary)]">
            <input type="checkbox" checked={chosen.has(v)} disabled={readOnly} className="h-3.5 w-3.5 accent-[color:var(--trodo-primary)]"
              onChange={(e) => setRule({ type: 'enum', values: e.target.checked ? [...chosen, v] : [...chosen].filter((x) => x !== v) })} />
            <span className="font-mono">{v}</span>
          </label>
        ))}
        {values.length === 0 && <span className="text-[12px] text-[color:var(--text-muted)]">Add categories first.</span>}
      </div>
      {values.length > 0 && chosen.size === 0 && <div className="mt-1 text-[11px] text-warning">Nothing passes yet. Tick at least one.</div>}
    </div>
  );
}

/** A decision check: per question, the answers that pass (a range for a score). Every question has to pass. */
function DecisionRuleEditor({ form, onForm, readOnly }) {
  const questions = form.node.config?.questions || {};
  const passes = form.rule.passes || {};
  const setPasses = (k, values) => onForm({ ...form, rule: { type: 'decision', passes: { ...passes, [k]: values } } });
  const entries = Object.entries(form.node.output || {});
  return (
    <div>
      <div className="mb-1.5 text-[12px] text-[color:var(--text-secondary)]">Passing answers</div>
      <div className="space-y-1.5">
        {entries.map(([k, spec]) => {
          const q = questions[k];
          if (spec.type === 'number') {
            const r = passes[k] && !Array.isArray(passes[k]) ? passes[k] : { min: spec.min, max: spec.max };
            const setR = (patch) => setPasses(k, { ...r, ...patch });
            return (
              <div key={k} className="flex flex-wrap items-center gap-2 text-[12px] text-[color:var(--text-primary)]" role="group" aria-label={`Passing range for ${k}`}>
                <span className="w-40 shrink-0 truncate font-mono" title={q?.instructions || k}>{k}</span>
                <span>≥</span>
                <input type="number" step="0.1" min={spec.min} max={spec.max} value={r.min} disabled={readOnly} aria-label={`${k} at least`} className={numInput}
                  onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) setR({ min: Math.max(spec.min, Math.min(v, r.max)) }); }} />
                <span>and ≤</span>
                <input type="number" step="0.1" min={spec.min} max={spec.max} value={r.max} disabled={readOnly} aria-label={`${k} at most`} className={numInput}
                  onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) setR({ max: Math.min(spec.max, Math.max(v, r.min)) }); }} />
                <span className="text-[color:var(--text-muted)]">of {spec.min}–{spec.max}</span>
              </div>
            );
          }
          const chosen = new Set(passes[k] || []);
          // Can't tell fails; it is only listed when a pass set already holds it, so it can be taken out.
          const shown = (spec.values || []).filter((v) => v !== 'cant_tell' || chosen.has(v));
          return (
            <div key={k}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" role="group" aria-label={`Passing answers for ${k}`}>
                <span className="w-40 shrink-0 truncate font-mono text-[12px] text-[color:var(--text-primary)]" title={q?.instructions || k}>{k}</span>
                {shown.map((v) => (
                  <label key={v} className="flex items-center gap-1.5 text-[12px] text-[color:var(--text-primary)]">
                    <input type="checkbox" checked={chosen.has(v)} disabled={readOnly} className="h-3.5 w-3.5 accent-[color:var(--trodo-primary)]"
                      onChange={(e) => setPasses(k, e.target.checked ? [...chosen, v] : [...chosen].filter((x) => x !== v))} />
                    <span>{q ? outcomeLabel(q, v) : v}</span>
                  </label>
                ))}
              </div>
              {chosen.size === 0 && <div className="mt-1 text-[11px] text-warning">Nothing passes for {k}. Tick at least one.</div>}
            </div>
          );
        })}
        {entries.length === 0 && <span className="text-[12px] text-[color:var(--text-muted)]">Add a question first.</span>}
      </div>
    </div>
  );
}

export default function SimpleEvalForm({
  graph, onChange, target = 'run', schema, scopes = null, integrations = [], modelsFor = () => [], teamId = null, agentName = null, readOnly = false,
}) {
  const form = readSimple(graph);
  // A judge with no provider yet takes the team's most recent one and its default model: one less thing to pick.
  const needsModel = form && form.kind === 'llm_judge' && form.node.config?.integration_id == null && integrations.length > 0 && !readOnly;
  useEffect(() => {
    if (!needsModel) return;
    // The provider the team touched last, with a key: the likeliest one they mean.
    const usable = integrations.filter((i) => i.has_api_key !== false);
    const first = [...(usable.length ? usable : integrations)].sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))[0];
    const models = modelsFor(first) || [];
    const model = first.default_model && (!models.length || models.includes(first.default_model)) ? first.default_model : models[0];
    onChange(buildSimple({ ...form, node: { ...form.node, config: { ...form.node.config, integration_id: first.id, ...(model ? { model } : {}) } } }));
  }, [needsModel]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!form) {
    return (
      <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-3 text-[12px] text-[color:var(--text-secondary)]" role="status">
        This eval has more than one step, or routes somewhere other than Pass and Fail. Switch to Triage in Settings to edit it.
      </div>
    );
  }
  const emit = (next) => { if (!readOnly) onChange(buildSimple(next)); };
  const updateNode = (patch) => {
    // The step's own editor may change its output (a grader's options): read the answer back from it.
    const node = { ...form.node, ...patch };
    const next = { ...form, node };
    if (form.kind === 'decision_model') { emit(patch.output ? withDecisionOutput(next, node) : next); return; }
    const entries = Object.entries(node.output || {});
    if (patch.output && entries.length === 1) {
      const [field, spec] = entries[0];
      emit({ ...withAnswer(next, spec), field });
      return;
    }
    emit(next);
  };
  // KindEditor reads a graph and a node id for the fields rail; hand it the form's own.
  const shim = { ...graph, nodes: { ...graph.nodes, [form.stepId]: form.node } };

  return (
    <div className="mx-auto max-w-3xl px-5 py-4" data-testid="simple-eval">
      <Section title="Eval type" hint="what runs on every matching event">
        <KindPicker value={form.kind} readOnly={readOnly} onPick={(k) => { if (k !== form.kind) onChange(startSimple(k, target)); }} />
      </Section>
      <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0 }}>
        <KindEditor kind={form.kind} node={form.node} update={updateNode} graph={shim} nodeId={form.stepId} readOnly={readOnly}
          schema={schema} scopes={scopes} target={target} integrations={integrations} modelsFor={modelsFor} teamId={teamId} agentName={agentName} />
      </fieldset>
      {form.kind !== 'decision_model' && (
        <Section title="Output" hint="what the check returns: true/false, a score in a range, or one of some categories">
          <AnswerEditor form={form} onForm={emit} readOnly={readOnly} />
        </Section>
      )}
      <Section title="Decision" hint="which answers pass, both ends of a range included; the rest fail with the reason below">
        {form.kind === 'decision_model'
          ? <DecisionRuleEditor form={form} onForm={emit} readOnly={readOnly} />
          : <RuleEditor form={form} onForm={emit} readOnly={readOnly} />}
        <div className={`${line} mt-3`}>
          <span>Fail reason</span>
          <input value={form.failReason} readOnly={readOnly} aria-label="Fail reason" placeholder="LOW_SCORE"
            onChange={(e) => emit({ ...form, failReason: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
            className={`${inputBox} w-48 font-mono`} />
        </div>
      </Section>
    </div>
  );
}
